"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import Image from "next/image"
import { X, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { Spinner } from "@/components/ui/spinner"

const ACCEPTED_FORMATS = {
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/wave': ['.wav'],
  'audio/x-wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/mp4': ['.m4a', '.aac'],
  'audio/x-m4a': ['.m4a'],
  'audio/aac': ['.aac'],
  'audio/webm': ['.webm'],
}

const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm']
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB limit for source files (§19.3)
const accentDragClassName =
  "border-2 border-[color:var(--tls-brand)] bg-[color:color-mix(in_srgb,var(--tls-brand)_8%,transparent)]"
// The tray is only an interactive drop-zone/button before a file exists; once a
// file is selected it becomes a plain container so the nested Remove/Replace
// buttons are not invalid nested interactive elements.

// Single-ink raster and its dark twin, per the dark-twin naming convention
// (`<name>-dark.png`, light ink on transparent). `.tls-img-light` /
// `.tls-img-dark` in app/globals.css show exactly one of the pair per theme.
const EMPTY_GLYPH_LIGHT = "/images/guide/generated/convert-dropzone-empty.png"
const EMPTY_GLYPH_DARK = "/images/guide/generated/convert-dropzone-empty-dark.png"

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

interface FileDropzoneProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  error?: string | null
  selectedFile?: File | null
  onClear?: () => void
  className?: string
}

export function FileDropzone({
  onFileSelect,
  isLoading = false,
  error = null,
  selectedFile = null,
  onClear,
  className
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const alertRef = useRef<HTMLDivElement>(null)

  // §19.3 / docs/ANTI_SLOP.md: name what happened and what to do, using the
  // real file's name/size/format rather than a generic "unsupported format".
  const validateFile = useCallback((file: File): string | null => {
    // Check file size — names the actual size, not just "too large"
    if (file.size > MAX_FILE_SIZE) {
      const mb = (file.size / 1024 / 1024).toFixed(1)
      return `That file is ${mb} MB, the limit is 20. A five-second clip is never that big, so this is probably a full song. Trim it first, or drop a shorter export.`
    }

    // Zero-byte file: a distinct cause from a bad format
    if (file.size === 0) {
      return `${file.name} is empty. It contains no audio data, which usually means the download failed part way.`
    }

    // Check file type — names the real extension, not the generic word "format"
    const extension = '.' + (file.name.split('.').pop()?.toLowerCase() || '')
    const isValidExtension = ACCEPTED_EXTENSIONS.includes(extension)
    const isValidMime = Object.keys(ACCEPTED_FORMATS).includes(file.type)

    if (!isValidExtension && !isValidMime) {
      const isImage = file.type.startsWith('image/')
      if (isImage) {
        return `${file.name} is an image. Audio only, try MP3, M4A, WAV, OGG, FLAC or AAC.`
      }
      return `We cannot read ${extension} files. MP3, M4A, WAV, OGG, FLAC and AAC work, and ${extension.slice(1).toUpperCase() || 'that format'} is not one of them.`
    }

    return null
  }, [])

  const handleFile = useCallback((file: File) => {
    setValidationError(null)

    const error = validateFile(file)
    if (error) {
      setValidationError(error)
      return
    }

    onFileSelect(file)
  }, [validateFile, onFileSelect])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if we're leaving the dropzone entirely
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFile(files[0])
    }
  }, [handleFile])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFile(files[0])
    }
    // Reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [handleFile])

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  // §19.3 keyboard contract: "Drag-and-drop is never the only route. Paste
  // is also accepted — Cmd/Ctrl+V with an audio file on the clipboard loads
  // it." The clause reads as document-level, so while the editor has no
  // file the listener lives on `document`: the user does not have to find
  // and focus the zone first. A paste aimed at a text field is left alone,
  // and one another handler already consumed is not re-read.
  useEffect(() => {
    if (selectedFile || isLoading) return
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented || isEditableTarget(e.target)) return
      const files = e.clipboardData?.files
      if (files && files.length > 0) {
        e.preventDefault()
        handleFile(files[0])
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [selectedFile, isLoading, handleFile])

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setValidationError(null)
    onClear?.()
  }, [onClear])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const displayError = error || validationError

  // §19.3: "Each failure is role=alert and receives focus, because the user
  // must read it before continuing." role=alert announces; this moves focus.
  useEffect(() => {
    if (displayError) alertRef.current?.focus()
  }, [displayError])

  return (
    <div className={cn("w-full", className)}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        onChange={handleInputChange}
        className="hidden"
        aria-label="Upload audio file"
      />

      {/* eslint-disable-next-line react-doctor/prefer-tag-over-role -- empty drop zone is also a drag target; <button> does not support drag/drop idiomatically. Once a file is selected the interactive props drop off so the nested Remove/Replace buttons are valid. */}
      <div
        onClick={!selectedFile && !isLoading ? handleClick : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "relative min-h-[220px] border border-dashed p-8 transition-[border-color,background-color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
          "flex flex-col items-center justify-center gap-4",
          !selectedFile && !isLoading && "cursor-pointer",
          isDragging && accentDragClassName,
          displayError && "border-2 border-[color:var(--tls-fg-strong)]",
          !isDragging && !displayError && !selectedFile && "border-[color:var(--tls-border-subtle)] hover:border-[color:var(--tls-border-strong)]",
          selectedFile && !displayError && "border-[color:var(--tls-border-strong)]"
        )}
        role={selectedFile ? undefined : "button"}
        tabIndex={selectedFile ? undefined : 0}
        aria-label={selectedFile ? undefined : "Choose an audio file, or drop or paste one here"}
        onKeyDown={selectedFile ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }}
      >
        {isLoading ? (
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" className="text-[var(--tls-brand)]" />
            <p className="text-sm text-[var(--tls-fg-muted)]">Analyzing audio…</p>
          </div>
        ) : selectedFile ? (
          <div className="flex flex-col items-center gap-2 w-full">
            <div className="flex w-full max-w-md items-center gap-3 border-y border-[color:var(--tls-border-subtle)] py-3">
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-[var(--tls-fg-strong)]">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-[var(--tls-fg-subtle)]">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
              {/* 44×44: this is the mobile-first tool, and a thumb is expected here. */}
              <button type="button"
                onClick={handleClear}
                className="tlsr-focus flex size-11 flex-shrink-0 items-center justify-center rounded-[var(--ls-radius-0)] transition-colors hover:bg-[var(--tls-surface-hover)]"
                aria-label="Remove file"
              >
                <X className="size-4 text-[var(--tls-fg-subtle)]" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleClick}
              className="tlsr-focus inline-flex min-h-[var(--touch-target-min)] items-center px-3 text-sm font-medium text-[var(--tls-brand-ink)] underline-offset-2 hover:underline"
            >
              Replace file
            </button>
          </div>
        ) : (
          <>
            <div className={cn(
              "flex size-16 items-center justify-center border border-[color:var(--tls-border-subtle)] transition-colors",
              isDragging ? "border-[color:var(--tls-brand)] text-[var(--tls-brand)]" : "text-[var(--tls-fg-subtle)]"
            )}>
              <Image
                src={EMPTY_GLYPH_LIGHT}
                alt=""
                width={64}
                height={64}
                aria-hidden="true"
                className="tls-img-light size-10 object-contain"
              />
              <Image
                src={EMPTY_GLYPH_DARK}
                alt=""
                width={64}
                height={64}
                aria-hidden="true"
                className="tls-img-dark size-10 object-contain"
              />
            </div>

            <div className="text-center">
              <p className="mb-1 font-medium text-[var(--tls-fg-strong)]">
                {isDragging ? "Drop your audio file here" : "Drag & drop your audio file"}
              </p>
              <p className="text-sm text-[var(--tls-fg-muted)]">
                or <span className="text-[var(--tls-brand-ink)] hover:underline">browse</span> to choose
              </p>
            </div>

            {/* The format row is the hint; a failure replaces it rather than
                printing over it (§19.3 draws each failure as its own state). */}
            {!displayError && (
              <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
                {ACCEPTED_EXTENSIONS.map((ext) => (
                  <span
                    key={ext}
                    className="text-xs text-[var(--tls-fg-subtle)]"
                  >
                    {ext.toUpperCase().slice(1)}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {/* In flow, after whichever state is showing. role="alert" announces
            it as soon as it appears; tabIndex=-1 + the focus effect above
            move the reader to it, per §19.3. */}
        {displayError && (
          <div
            ref={alertRef}
            tabIndex={-1}
            role="alert"
            aria-live="assertive"
            className="tlsr-focus w-full max-w-md"
          >
            <div className="flex items-start gap-2 py-2">
              <AlertCircle className="mt-0.5 size-4 flex-shrink-0 text-[var(--tls-fg-strong)]" aria-hidden="true" />
              <p className="text-sm text-[var(--tls-fg-strong)]">{displayError}</p>
            </div>
          </div>
        )}
      </div>

      <p className="mt-3 text-center text-xs text-[var(--tls-fg-subtle)]">
        Supported formats: MP3, WAV, OGG, M4A, AAC, WebM (max 20MB)
      </p>
    </div>
  )
}
